import { useState, useEffect, useCallback, type Dispatch, type SetStateAction } from 'react';

/** Admin / manager / worker shell: persistent sidebar from this width up; drawer below. */
export const DASHBOARD_DESKTOP_SIDEBAR_MEDIA = '(min-width: 1024px)' as const;

function getInitialDesktopMatch(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(DASHBOARD_DESKTOP_SIDEBAR_MEDIA).matches;
}

export type DashboardSidebarLayout = {
    /** Drawer open (tablet/mobile) or aligned with desktop column (≥1024px); user can toggle only in drawer mode. */
    isSidebarOpen: boolean;
    setSidebarOpen: Dispatch<SetStateAction<boolean>>;
    /** Viewport is desktop breakpoint; sidebar is not using overlay drawer layout. */
    isDesktop: boolean;
};

/**
 * Single source of truth for shell sidebar + breakpoint.
 * `change` fires only when crossing 1024px — not on every resize pixel.
 * On cross: both `isDesktop` and `isSidebarOpen` sync to `e.matches` (open on desktop, closed in drawer mode).
 * Manual `setSidebarOpen` only updates drawer open state and does not change `isDesktop`.
 */
export function useSidebarOpenForDashboardLayout(): DashboardSidebarLayout {
    const [isDesktop, setIsDesktop] = useState(getInitialDesktopMatch);
    const [isSidebarOpen, setSidebarOpenState] = useState(getInitialDesktopMatch);

    useEffect(() => {
        const mql = window.matchMedia(DASHBOARD_DESKTOP_SIDEBAR_MEDIA);
        const applyBreakpoint = (matches: boolean) => {
            setIsDesktop(matches);
            setSidebarOpenState(matches);
        };
        applyBreakpoint(mql.matches);

        const onChange = (e: MediaQueryListEvent) => {
            applyBreakpoint(e.matches);
        };
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    const setSidebarOpen = useCallback((value: SetStateAction<boolean>) => {
        setSidebarOpenState(value);
    }, []);

    return { isSidebarOpen, setSidebarOpen, isDesktop };
}
